import { Divider, Drawer, Input, message, Switch } from 'antd'
import clsx from 'clsx'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  FC,
} from 'react'
import styles from './Create.module.css'
import {
  CreateCharacterTagsNSFW,
  CreateCharacterTagsSFW,
  TagLookupNSFW,
  TagLookupSFW,
  filterTagsForUpdatedCharacter,
} from '@/constant/tags'
import {
  MAX_DIALOG_STYLE_LENGTH,
  MAX_GREETING_LENGTH,
  MAX_LONG_DESCRIBE_LENGTH,
  MAX_NAME_LENGTH,
  MAX_OPTIMIZATION_CODE_LENGTH,
  MAX_REPLY_REQUIREMENT_LENGTH,
  MAX_SCENARIO_LENGTH,
  MAX_SHORT_DESCRIBE_LENGTH,
  MIN_GREETING_LENGTH,
  UPLOAD_IMG_CROP_RATIO,
} from '@/constant/limit'
import TipsyButton from 'Comps/ui-kit/TipsyButton'
import {
  CreateExitMobileIcon,
  CreateRulesIcon,
  CreateSelectedIcon,
  CreateSelectedMobileIcon,
  NavBackOutlinedMobileIcon,
  TagEventIcon,
  TagNewIcon,
} from '@/icons'
import useCreateDetails from '@/hooks/create/useCreateDetails'
import { MOBILE_PAGE_PADDING } from '@/constant/commonStyle'
import useI18n from 'Hooks/useI18n'
import useCreateConversation from '@/hooks/create/useCreateConversation'
import {
  AntDesignOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
  ArrowDownIcon,
  Christmas2025tag,
  Valentines2026tag,
  CloseOutlinedIcon,
  DeleteIcon,
  Halloween2025tag,
  LiveImageIcon,
} from '@/components/svg'
import Image from 'next/image'
import { transformImage } from '@/utils/img'
import { ProfileEditCharIcon } from '@/components/svg/ProfileEditCharIcon'
import { estimateTokenCount } from '@/utils/func'
import useCreateImg from '@/hooks/create/useCreateImg'
import { useRecoilState, useRecoilValue } from 'recoil'
import { createStateFactory } from '@/store/create'
import { userState } from '@/store/user'
import { withLocale } from '@/locales'
import { CreateIconGuide } from 'Comps/svg'
import Link from 'next/link'
import useVoices from '@/hooks/useVoices'
import { VoiceItem } from '@/apis/voice'
import VoicePlayButton, {
  AudioProvider,
} from '@/components/common/VoicePlayButton'
import {
  ChatInspirationZoomLightIcon,
  ChatInspirationCollapseLightIcon,
} from '@/components/svg/enhanced'
import CharacterDraftModal from '@/components/common/CharacterDraftModal'
import useCharacterDraftBox from '@/hooks/create/useCharacterDraftBox'
import RestoreLocalDraftModal from '@/components/common/RestoreLocalDraftModal'
import useLocalCharacterDraftCache from '@/hooks/create/useLocalCharacterDraftCache'
import TagDisplayLabel from '@/components/common/TagDisplayLabel'
import { resolveTagDisplaysByIds } from '@/utils/tagDisplay'

import { IdList, ProfileCharacterList, SelfStory } from '@/types/character'
import useProfileCharacters from '@/hooks/profile/useProfileCharacters'
import { ReviewStage } from '@/types/review'
import ProfileLockIcon from '@/components/svg/ProfileLockIcon'
import { BackgroundFileUploader } from '@/components-mobile/create/BackgroundFileUploader'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import { getPlatform } from '@/utils/platform'
import { getCharacterPublicByUser } from '@/apis/character'
import GreetingVideoUploader from '@/components/create/GreetingVideoUploader'
import CharacterCreationConfirmation from '@/components/common/CharacterCreationConfirmation'
import useCharacterCreationConfirmation from '@/hooks/create/useCharacterCreationConfirmation'
import {
  createCharacterAnalytics,
  getCharacterCreateSession,
  retainCharacterCreateSessionForRouteTransition,
  startCharacterCreateSession,
} from '@/lib/analytics/characterCreateAnalytics'

type CreateDetailsProps = {
  setStep: Dispatch<SetStateAction<number>>
}

// Web 暂无问候视频消费链路，暂不展示 Intro Video 上传配置。
const ENABLE_WEB_GREETING_VIDEO_CONFIG = false

const buildSelectedIdList = (
  selectedIds: string[],
  characters: ProfileCharacterList[],
  fallbackList: IdList[]
) => {
  const fallbackMap = new Map(
    fallbackList.map((item) => [item.character_id, item] as const)
  )
  const characterMap = new Map(
    characters.map((item) => [item.character_id, item] as const)
  )

  return selectedIds
    .map((id) => {
      const fallback = fallbackMap.get(id)
      if (fallback) {
        return fallback
      }

      const matched = characterMap.get(id)
      if (!matched) {
        return null
      }

      return {
        character_id: matched.character_id,
        nickname: matched.nickname,
        image_url: matched.image_url,
      }
    })
    .filter((item): item is IdList => !!item)
}

export default function CreateDetailsMobile({ setStep }: CreateDetailsProps) {
  const { t, lang } = useI18n()
  const { trackEvent } = useQuickTracking()
  const platform = getPlatform()
  const [conversationDrawerOpen, setConversationDrawerOpen] = useState(false)
  const [tagDrawerOpen, setTagDrawerOpen] = useState(false)
  const [localSelectedTags, setLocalSelectedTags] = useState<string[]>([])
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [openingExpanded, setOpeningExpanded] = useState(false)
  const [backgroundExpanded, setBackgroundExpanded] = useState(false)
  const [replySettingExpanded, setReplySettingExpanded] = useState(false)
  const [layoutCustomizeExpanded, setLayoutCustomizeExpanded] = useState(false)
  const [outputSettingExpanded, setOutputSettingExpanded] = useState(false)
  const [greetingTokenCount, setGreetingTokenCount] = useState(0)
  const [backgroundTokenCount, setBackgroundTokenCount] = useState(0)
  const [outputSettingTokenCount, setOutputSettingTokenCount] = useState(0)
  const {
    isCreationConfirmed,
    showCreationConfirmationError,
    setCreationConfirmed,
    validateCreationConfirmation,
    resetCreationConfirmation,
  } = useCharacterCreationConfirmation()
  const {
    charImageUrl,
    charFaceImagePreviewUrl,
    charNickname,
    charGender,
    character_type,
    role_character_ids,
    role_character_list,
    plot_file_key,
    plot_file_name,
    plot_file_prefix,
    is_plot_file_uploading,
    charShortDescribe,
    styledTagline,
    charLongDescribe,
    showCharLongDescribe,
    replyRequirement,
    canShowReplySetting,
    contentType,
    charTagIDs,
    charScenario,
    charGreeting,
    styledGreeting,
    advancedSetCheck,
    conversationStyleData,
    conversationStyleKey,
    charDialogStyleFirstAllRoleMessage,
    nsfwCheck,
    setNsfwCheck,
    setCharNickname,
    setCharGender,
    setCharacterType,
    setChildCharacterIds,
    setRoleCharacterList,
    setPlotFileKey,
    setPlotFileName,
    setPlotFilePrefix,
    setIsPlotFileUploading,
    world_books,
    addWorldBook,
    removeWorldBook,
    setIsWorldBookUploading,
    setUserRoleName,
    setUserRoleProfile,
    setUserRoleImage,
    setUserRoleImageUrl,
    setUserRoleFaceUrl,
    setCharShortDescribe,
    setStyledTagline,
    setCharLongDescribe,
    setShowCharLongDescribe,
    setReplyRequirement,
    setContentType,
    setAdvancedSetCheck,
    setCharScenario,
    setConversationStyleKey,
    setCharGreeting,
    setStyledGreeting,
    setCharTagIDs,
    goPrevStep,
    nextButtonDisabled,
    handleDialogInputChange,
    isCharNameError,
    isCharShortDescribeError,
    isCharLongDescribeError,
    isReplyRequirementError,
    isScenarioError,
    isCharGreetingError,
    isCharDialogStyleError,
    optimizationCode,
    outputFormatConfig,
    setOptimizationCode,
    setOutputFormatConfig,
  } = useCreateDetails({ setStep, enableCustomPrompt: true })
  const tagOptions = nsfwCheck
    ? CreateCharacterTagsNSFW
    : CreateCharacterTagsSFW
  const selectedTagOptions = nsfwCheck ? TagLookupNSFW : TagLookupSFW
  const selectedTagItems = resolveTagDisplaysByIds(
    charTagIDs,
    selectedTagOptions
  )
  const { setIsPreviewStep, clearCharImage, isPreviewStep } = useCreateImg({
    setStep,
    scene: 'character_image',
  })

  const router = useRouter()
  const { charIsPublic, setCharIsPublic, handleCreate, createdLoading } =
    useCreateConversation({
      setStep,
      enableCustomPrompt: true,
      validateCreationConfirmation,
    })

  useEffect(() => {
    let cancelled = false

    const updateTokenCounts = async () => {
      const [greetingCount, backgroundCount, outputCount] = await Promise.all([
        estimateTokenCount(charGreeting),
        estimateTokenCount(charLongDescribe || ''),
        estimateTokenCount(outputFormatConfig || ''),
      ])

      if (cancelled) return

      setGreetingTokenCount(greetingCount)
      setBackgroundTokenCount(backgroundCount)
      setOutputSettingTokenCount(outputCount)
    }

    void updateTokenCounts()

    return () => {
      cancelled = true
    }
  }, [charGreeting, charLongDescribe, outputFormatConfig])

  const searchParams = useSearchParams()
  const from = searchParams.get('from')
  const preselectTag = searchParams.get('preselect_tag')
  const isEdit = searchParams.get('isEdit') === 'true'
  const characterId = searchParams.get('character_id')
  const [createCharForm, setCreateCharForm] = useRecoilState(
    createStateFactory(false, from || 'character_image')
  )
  const [updateCharForm, setUpdateCharForm] = useRecoilState(
    createStateFactory(true, from || 'character_image')
  )
  const pathname = usePathname()
  const isUpdateChar =
    pathname === withLocale('/update', lang) ||
    searchParams.get('update') === 'true'
  const charForm = isUpdateChar ? updateCharForm : createCharForm
  const setCharForm = isUpdateChar ? setUpdateCharForm : setCreateCharForm
  const visibleTagOptions = useMemo(
    () =>
      isUpdateChar
        ? filterTagsForUpdatedCharacter(
            tagOptions,
            charForm.charCreatedAt,
            localSelectedTags
          )
        : tagOptions,
    [charForm.charCreatedAt, isUpdateChar, localSelectedTags, tagOptions]
  )
  const showFormatField =
    isUpdateChar && character_type === 1 && contentType === 2
  const showHtmlStylingSwitch = character_type === 1 && contentType !== 2
  const isLegacyHtml = contentType === 2
  const isStyledMarkdown = contentType === 3
  const isRichDisplayContent = isLegacyHtml || isStyledMarkdown
  const displayDescription = isRichDisplayContent
    ? styledTagline
    : charShortDescribe
  const displayOpening = isRichDisplayContent ? styledGreeting : charGreeting
  const isDisplayDescriptionError =
    displayDescription.length > MAX_SHORT_DESCRIBE_LENGTH
  const isDisplayOpeningError = displayOpening.length > MAX_GREETING_LENGTH
  const user = useRecoilValue(userState)
  const characterAnalytics = useMemo(
    () =>
      createCharacterAnalytics({
        userId: user?.user_id,
        platform: 'web_h5',
        trackEvent,
      }),
    [trackEvent, user?.user_id]
  )
  const exposedCreateSessionIdRef = useRef('')
  const trackCreatePageExposure = useCallback(() => {
    const sessionId = getCharacterCreateSession()?.createSessionId
    if (!sessionId || exposedCreateSessionIdRef.current === sessionId) return

    const result = characterAnalytics.pageExposure()
    if (
      result.status === 'sent' ||
      (result.status === 'suppressed' && result.reason === 'duplicate_event')
    ) {
      exposedCreateSessionIdRef.current = sessionId
    }
  }, [characterAnalytics])

  useEffect(() => {
    trackCreatePageExposure()
  }, [trackCreatePageExposure])
  const greetingVideoKey = charForm.greetingVideoKey
  const greetingVideoUrl = charForm.greetingVideoUrl
  const greetingVideoUploading = charForm.greetingVideoUploading
  const {
    draftModalOpen,
    setDraftModalOpen,
    draftLoading,
    draftDeletingId,
    draftItems,
    draftTotal,
    draftSaving,
    saveCurrentDraft,
    loadDraftById,
    deleteDraftById,
  } = useCharacterDraftBox({
    charForm: greetingVideoUploading
      ? {
          ...charForm,
          greetingVideoKey: undefined,
          greetingVideoUrl: undefined,
        }
      : charForm,
    setCharForm,
    isUpdateChar,
    t,
  })
  const {
    restoreModalOpen,
    restoreCache,
    discardCache,
    clearCache: clearLocalDraftCache,
  } = useLocalCharacterDraftCache({
    charForm: greetingVideoUploading
      ? {
          ...charForm,
          greetingVideoKey: undefined,
          greetingVideoUrl: undefined,
        }
      : charForm,
    setCharForm,
    userId: user?.user_id,
    isUpdateChar,
    source: from || 'character_image',
    characterId,
    cacheEnabled: !isUpdateChar,
  })
  const handleSaveCurrentDraft = useCallback(async () => {
    characterAnalytics.saveClick()
    if (!isUpdateChar && !validateCreationConfirmation()) return

    const saved = await saveCurrentDraft()
    if (saved) {
      clearLocalDraftCache()
    }
  }, [
    clearLocalDraftCache,
    characterAnalytics,
    isUpdateChar,
    saveCurrentDraft,
    validateCreationConfirmation,
  ])
  const handleLoadDraftById = useCallback(
    async (draftId: string) => {
      if (!isUpdateChar) {
        resetCreationConfirmation()
      }
      const loaded = await loadDraftById(draftId)
      if (!loaded) return

      startCharacterCreateSession({
        triggerSource: 'draft_box',
        operationType: isUpdateChar ? 'update' : 'create',
        forceNewSession: true,
      })
      trackCreatePageExposure()
    },
    [
      isUpdateChar,
      loadDraftById,
      resetCreationConfirmation,
      trackCreatePageExposure,
    ]
  )
  const handleRestoreCache = useCallback(async () => {
    resetCreationConfirmation()
    await restoreCache()
  }, [resetCreationConfirmation, restoreCache])
  const { uniqueCharacterData } = useProfileCharacters(true)
  const publicCharacterData = useMemo(() => {
    return (uniqueCharacterData as SelfStory[])
      .filter((item) => item.item_type === 'character')
      .map((item) => item.character)
      .filter(
        (item): item is ProfileCharacterList =>
          !!item && item.character_type === 1
      )
  }, [uniqueCharacterData])

  const selectedRoleList = useMemo(
    () =>
      buildSelectedIdList(
        role_character_ids || [],
        publicCharacterData,
        role_character_list || []
      ),
    [publicCharacterData, role_character_ids, role_character_list]
  )

  const characterStatusById = useMemo(() => {
    const statusMap = new Map<
      string,
      { is_public?: boolean; review_stage?: ReviewStage }
    >()
    ;(uniqueCharacterData as SelfStory[]).forEach((item) => {
      if (item.item_type !== 'character' || !item.character) return
      statusMap.set(item.character.character_id, {
        is_public: item.character.is_public,
        review_stage: item.character.review_stage,
      })
    })
    return statusMap
  }, [uniqueCharacterData])
  const hasProfileLock = useMemo(() => {
    if (charIsPublic !== 'true') return false
    return selectedRoleList.some((item) => {
      const characterStatus = characterStatusById.get(item.character_id)
      return (
        characterStatus?.is_public !== true ||
        characterStatus?.review_stage !== ReviewStage.Pass
      )
    })
  }, [charIsPublic, characterStatusById, selectedRoleList])

  const hasNsfwCharacter = useMemo(() => {
    if (character_type !== 2) return false
    return selectedRoleList.some((selected) => {
      const char = publicCharacterData.find(
        (c) => c.character_id === selected.character_id
      )
      return char?.nsfw === true
    })
  }, [character_type, publicCharacterData, selectedRoleList])

  useEffect(() => {
    if (hasNsfwCharacter) {
      setNsfwCheck(true)
    }
  }, [hasNsfwCharacter, setNsfwCheck])

  useEffect(() => {
    setLocalSelectedTags(charTagIDs)
  }, [charTagIDs])

  const roleCharacterHref = useMemo(() => {
    const params = new URLSearchParams()
    if (isEdit) {
      params.set('isEdit', 'true')
    }
    if (isUpdateChar) {
      params.set('update', 'true')
    }
    if (from) {
      params.set('from', from)
    }
    if (characterId) {
      params.set('character_id', characterId)
    }
    const query = params.toString()
    return withLocale(
      query ? `/create/roleCharacter?${query}` : '/create/roleCharacter',
      lang
    )
  }, [characterId, from, isEdit, isUpdateChar, lang])

  const plotUploaderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = plotUploaderRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          trackEvent('plot_uploader_view', { platform }, 'EXP')
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [platform, trackEvent])

  const hasDraftRoleCharacterSnapshot =
    character_type === 2 &&
    !!charForm.draftLoadedVersion &&
    ((role_character_ids?.length || 0) > 0 ||
      (role_character_list?.length || 0) > 0)

  // 编辑 character_type=2 时，调用 getCharacterPublicByUser 获取子角色列表
  // （剧本/世界书回显在 useCreateDetails 的共享 effect 中处理，单/多角色一致，带三态防覆盖）
  useEffect(() => {
    if (
      !isUpdateChar ||
      character_type !== 2 ||
      !characterId ||
      hasDraftRoleCharacterSnapshot
    ) {
      return
    }
    let cancelled = false
    const fetchCharacterData = async () => {
      try {
        const res = await getCharacterPublicByUser(characterId, true, lang)
        if (cancelled) return
        // role_characters 在响应顶层，包含完整的子角色信息
        if (res.role_characters?.length) {
          const childIds = res.role_characters.map((c) => c.character_id)
          setChildCharacterIds(childIds)
          const builtIdList: IdList[] = res.role_characters.map((c) => ({
            character_id: c.character_id,
            nickname: c.nickname,
            image_url: c.image_url,
          }))
          setRoleCharacterList(builtIdList)
        }
      } catch (error) {
        console.error('Failed to fetch character data for edit:', error)
      }
    }
    fetchCharacterData()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateChar, character_type, characterId, hasDraftRoleCharacterSnapshot])

  const handleDeleteUser = () => {
    setUserRoleName('')
    setUserRoleProfile('')
    setUserRoleImageUrl('')
    setUserRoleImage('')
    setUserRoleFaceUrl('')
  }

  const removeCharacter = (id: string) => {
    const newCharacterIds = (role_character_ids || []).filter(
      (item) => item !== id
    )
    setChildCharacterIds(newCharacterIds)
    setRoleCharacterList(
      selectedRoleList.filter((item) => item.character_id !== id)
    )
  }

  const renderCharacter = (item: IdList) => {
    const characterStatus = characterStatusById.get(item.character_id)
    const showProfileLock =
      charIsPublic === 'true' &&
      (characterStatus?.is_public !== true ||
        characterStatus?.review_stage !== ReviewStage.Pass)
    return (
      <div
        className="flex max-w-[68px] flex-col items-center"
        key={item.character_id}
      >
        <div className="relative">
          <img
            src={item.image_url || ''}
            className="h-[68px] w-[68px] rounded-full object-cover"
            alt={item.nickname || ''}
          />
          {showProfileLock && (
            <ProfileLockIcon
              className="absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2"
              size={36}
            />
          )}

          {!isEdit && (
            <div
              className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#000000] opacity-50"
              onClick={() => removeCharacter(item.character_id)}
            >
              <CloseOutlinedIcon strokeWidth={2} size={20} />
            </div>
          )}
        </div>
        <div className="mt-2.5 w-full truncate text-center text-sm text-white">
          {item.nickname}
        </div>
      </div>
    )
  }

  // const renderUser = () => {
  //   return (
  //     <div className="flex flex-col items-center">
  //       {user_role_image_url ? (
  //         <div className="relative">
  //           <img
  //             src={user_role_face_url || user_role_image_url}
  //             className="h-[68px] w-[68px] rounded-full object-cover"
  //             alt=""
  //           />
  //           {!isEdit && (
  //             <button
  //               type="button"
  //               className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#9C4844] opacity-50"
  //               onClick={handleDeleteUser}
  //             >
  //               <CloseOutlinedIcon strokeWidth={2} size={20} />
  //             </button>
  //           )}
  //           <button
  //             type="button"
  //             className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#446D9C] opacity-50"
  //             onClick={() =>
  //               router.push(
  //                 withLocale(`/create/storyUser?isEdit=${isEdit}`, lang)
  //               )
  //             }
  //           >
  //             <ProfileEditCharIcon size={13} />
  //           </button>
  //         </div>
  //       ) : (
  //         <button
  //           type="button"
  //           className="flex h-[68px] w-[68px] items-center justify-center rounded-full border-2 border-dashed border-white/20 bg-white/5"
  //           onClick={() =>
  //             router.push(
  //               withLocale(`/create/storyUser?isEdit=${isEdit}`, lang)
  //             )
  //           }
  //         >
  //           <UserOutlined
  //             style={{
  //               fontSize: '24px',
  //               color: 'rgba(255,255,255,0.8)',
  //             }}
  //           />
  //         </button>
  //       )}
  //       <span className="mt-2.5 text-sm text-white">{t('User')}</span>
  //     </div>
  //   )
  // }

  // 自动选中指定标签
  useEffect(() => {
    if (!preselectTag || isUpdateChar) return

    const targetTag = tagOptions.find(
      (tag) =>
        tag.tag === preselectTag || tag.tag === preselectTag.replace(/\s/g, '')
    )

    if (targetTag && !charTagIDs.includes(targetTag.id)) {
      setCharTagIDs([...charTagIDs, targetTag.id])
    }
  }, [preselectTag, isUpdateChar, charTagIDs, setCharTagIDs, tagOptions])

  // 获取选中声音的详细信息
  const { voices: selectedVoices } = useVoices(
    charForm.charBaseTone
      ? {
          voice_id: charForm.charBaseTone,
          size: 1,
          page: 0,
        }
      : { size: 0 }
  )

  const goNextStep = () => {
    if (charNickname === '') {
      message.error({
        content: t('Name is required'),
        icon: <ExclamationCircleOutlined />,
      })
      return
    }
    if (charGender === null) {
      message.error({
        content: t('Gender is required'),
        icon: <ExclamationCircleOutlined />,
      })
      return
    }
    if (displayDescription === '') {
      message.error({
        content: t('Short Description is required'),
        icon: <ExclamationCircleOutlined />,
      })
      return
    }
    if (greetingVideoUploading) {
      message.error({
        content: t('Please wait for the video upload to finish.'),
        icon: <ExclamationCircleOutlined />,
      })
      return
    }
    if (charNickname.length > MAX_NAME_LENGTH) {
      message.error({
        content: t('Name should be within {{expr}} characters', {
          expr: MAX_NAME_LENGTH,
        }),
        icon: <ExclamationCircleOutlined />,
      })
      return
    }
    // if (charLongDescribe.length > MAX_LONG_DESCRIBE_LENGTH) {
    //   message.error({
    //     content: `Long Description should be within ${MAX_LONG_DESCRIBE_LENGTH} characters`,
    //     icon: <ExclamationCircleOutlined />,
    //   })
    //   return
    // }
    if (displayDescription.length > MAX_SHORT_DESCRIBE_LENGTH) {
      message.error({
        content: t('Short Description should be within {{expr}} characters', {
          expr: MAX_SHORT_DESCRIBE_LENGTH,
        }),
        icon: <ExclamationCircleOutlined />,
      })
      return
    }
    handleCreate()
  }

  const replySettingBlock = canShowReplySetting ? (
    <div className="mb-5">
      <div className="flex items-center justify-between">
        <div className="relative text-sm">{t('Reply Setting')}</div>
        <div className="relative text-xs text-[#B3B3B3]">
          <span
            style={{
              color: isReplyRequirementError
                ? '#FF5F5F'
                : replyRequirement.length === 0
                  ? '#B3B3B3'
                  : '#fff',
            }}
          >
            {Intl.NumberFormat('en-US').format(replyRequirement.length)}
          </span>
          <span
            style={{
              color: isReplyRequirementError ? '#FF5F5F' : '#B3B3B3',
            }}
          >
            /{Intl.NumberFormat('en-US').format(MAX_REPLY_REQUIREMENT_LENGTH)}
          </span>
        </div>
      </div>
      <div
        className={clsx(
          'relative mt-2 rounded-lg bg-white/5 pb-11 pr-11 transition-[min-height] duration-300 ease-out',
          replySettingExpanded ? 'min-h-[320px]' : 'min-h-[120px]'
        )}
      >
        <Input.TextArea
          className={clsx(
            'placeholder:text-gray-756A6E text-xs !leading-[15px]',
            styles.input
          )}
          autoSize={
            replySettingExpanded ? { minRows: 14 } : { minRows: 8, maxRows: 8 }
          }
          variant="borderless"
          style={{
            resize: 'none',
            padding: '10px 12px',
            background: 'rgb(0 ,0 ,0,0)',
            color: '#FFFFFF',
            border: isReplyRequirementError
              ? '1px solid #9c4242'
              : '0px solid #d9d9d9',
            maxHeight: replySettingExpanded ? '400px' : '240px',
          }}
          placeholder={t(
            'Describe how this character should respond, including message length, level of detail, narrative format, tone, and writing style. These instructions will guide how the character speaks, writes, and reacts in chats.'
          )}
          onChange={(e) => setReplyRequirement(e.target.value)}
          value={replyRequirement}
          maxLength={MAX_REPLY_REQUIREMENT_LENGTH}
        />
        <button
          type="button"
          className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
          onClick={() => setReplySettingExpanded((v) => !v)}
          aria-label={replySettingExpanded ? t('Collapse') : t('Expand')}
        >
          {replySettingExpanded ? (
            <ChatInspirationCollapseLightIcon size={12} color="#FFFFFF" />
          ) : (
            <ChatInspirationZoomLightIcon size={18} color="#FFFFFF" />
          )}
        </button>
      </div>
      {isReplyRequirementError && (
        <div className="h-5 text-xs text-[#ff4d4f]">
          <span>
            {t('please keep your input within {{max}} characters', {
              max: MAX_REPLY_REQUIREMENT_LENGTH,
            })}
          </span>
        </div>
      )}
    </div>
  ) : null

  return (
    <AudioProvider>
      <div
        className="flex h-full flex-col"
        style={{
          paddingLeft: MOBILE_PAGE_PADDING,
          paddingRight: MOBILE_PAGE_PADDING,
        }}
      >
        <div className="relative w-full flex-1">
          <div className="flex h-[228px] flex-row items-center justify-center py-2.5 pl-2.5 pr-2">
            <div className={`relative h-full`} style={{ aspectRatio: 9 / 14 }}>
              {charImageUrl ? (
                <Image
                  unoptimized
                  src={transformImage(charImageUrl || '', undefined, 90)}
                  alt={/* i18n-ignore */ 'Character'}
                  className="overflow-hidden rounded-lg object-contain"
                  fill={true}
                />
              ) : (
                <div
                  className="flex h-full flex-col items-center justify-center gap-5 overflow-hidden rounded-lg bg-white/10"
                  onClick={() => {
                    characterAnalytics.avatarEntryClick()
                    retainCharacterCreateSessionForRouteTransition()
                    router.push(
                      withLocale(
                        `/create/image?isPreview=false&update=${isUpdateChar ? 'true' : 'false'}`,
                        lang
                      )
                    )
                  }}
                >
                  <LiveImageIcon size={28} color="rgba(255,255,255,0.5)" />
                  <span className="text-sm text-white/50">
                    {t('Create Avatar')}
                  </span>
                </div>
              )}
              {charImageUrl && (
                <div className="absolute bottom-2 right-[-35px] flex flex-col gap-3">
                  <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-black/30"
                    onClick={() => {
                      characterAnalytics.avatarEntryClick()
                      retainCharacterCreateSessionForRouteTransition()
                      router.push(
                        withLocale(
                          `/create/image?isPreview=true&update=${isUpdateChar ? 'true' : 'false'}`,
                          lang
                        )
                      )
                    }}
                  >
                    <ProfileEditCharIcon size={18} />
                  </button>
                  <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-black/30"
                    onClick={() => {
                      clearCharImage()
                      setCharForm((prev) => ({
                        ...prev,
                        uploadImgReference: null,
                        uploadImgReferencePreviewUrl: null,
                        generateImgReference: null,
                        generateImgReferencePreviewUrl: null,
                      }))
                    }}
                  >
                    <DeleteIcon size={20} />
                  </button>
                </div>
              )}
            </div>
          </div>
          <Link
            href={
              contentType === 2
                ? 'http://cdn.tipsy.chat/docs/html_creator_guide.pdf'
                : withLocale('/creators-guide', lang)
            }
            target="_blank"
            className="absolute right-0 top-3.5 flex items-center gap-1.5 text-xs text-white opacity-30 transition hover:opacity-50"
            style={{ fontSize: '12px' }}
          >
            <div className="translate-y-[1px]">
              <CreateIconGuide size={18} />
            </div>
            <span>{t("Creator's Guide")}</span>
          </Link>
          <div className="mt-5 flex items-center justify-between">
            <div className="relative text-sm">
              {t('Name')} <span className="text-[#ff5454]">*</span>
            </div>
            <div className="relative text-xs">
              <span
                style={{
                  color: isCharNameError
                    ? '#FF5F5F'
                    : charNickname.length === 0
                      ? '#B3B3B3'
                      : '#fff',
                }}
              >
                {charNickname.length}
              </span>
              <span
                style={{
                  color: isCharNameError ? '#FF5F5F' : '#B3B3B3',
                }}
              >
                /{MAX_NAME_LENGTH}
              </span>
            </div>
          </div>

          <div className="mt-2 h-[40px] rounded-lg bg-white/5">
            <Input
              className={clsx(
                'placeholder:text-gray-756A6E text-[13px]',
                styles.input
              )}
              style={{
                height: 40,
                resize: 'none',
                padding: '0 12px',
                background: 'rgb(0 ,0 ,0,0)',
                color: '#FFFFFF',
                border: '0px solid #d9d9d9',
              }}
              variant="borderless"
              maxLength={MAX_NAME_LENGTH}
              placeholder={t('A title or character name')}
              onChange={(e) => setCharNickname(e.target.value)}
              value={charNickname}
            ></Input>
          </div>
          <div className="h-4 text-xs text-[#ff4d4f]">
            {isCharNameError && (
              <span>
                {t('please keep your input within {{max}} characters', {
                  max: MAX_NAME_LENGTH,
                })}
              </span>
            )}
          </div>

          <div className="mb-4 flex items-center justify-between">
            <div className="relative text-sm">
              {t('Character Type')} <span className="text-[#ff5454]">*</span>
            </div>
            <div className="flex text-[13px]">
              <div
                className={clsx(
                  'relative ml-3.5 flex items-center',
                  isUpdateChar
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer',
                  character_type === 1 ? 'text-white' : 'text-[#B3B3B3]'
                )}
                onClick={() => {
                  if (!isUpdateChar) setCharacterType(1)
                }}
              >
                {character_type === 1 ? (
                  <div className="mr-1.5">
                    <CreateSelectedMobileIcon />
                  </div>
                ) : (
                  <div className="mr-1.5 h-3.5 w-3.5 rounded-full border-2 border-white/50"></div>
                )}
                {t('Character')}
              </div>
              <div
                className={clsx(
                  'relative ml-3.5 flex items-center',
                  isUpdateChar || contentType === 2
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer',
                  character_type === 2 ? 'text-white' : 'text-[#B3B3B3]'
                )}
                onClick={() => {
                  if (!isUpdateChar && contentType !== 2) {
                    setCharacterType(2)
                    setContentType(1)
                  }
                }}
              >
                {character_type === 2 ? (
                  <div className="mr-1.5">
                    <CreateSelectedMobileIcon />
                  </div>
                ) : (
                  <div className="mr-1.5 h-3.5 w-3.5 rounded-full border-2 border-white/50"></div>
                )}
                {t('Multi-character')}
              </div>
            </div>
          </div>

          {showFormatField && (
            <div className="mb-4 flex items-center justify-between">
              <div className="relative text-sm">
                {t('Format')} <span className="text-[#ff5454]">*</span>
              </div>
              <div className="flex text-[13px]">
                <div className="relative ml-3.5 flex cursor-not-allowed items-center text-white opacity-50">
                  <div className="mr-1.5">
                    <CreateSelectedMobileIcon />
                  </div>
                  {/* i18n-ignore */ 'HTML(Beta)'}
                </div>
              </div>
            </div>
          )}
          {showHtmlStylingSwitch && (
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <div className="relative text-sm">{t('HTML Styling')}</div>
                <Switch
                  className={clsx('!min-h-[19px] !min-w-[36px]', {
                    '!bg-white/20': !isStyledMarkdown,
                    '!bg-[#FF8A00]': isStyledMarkdown,
                  })}
                  size="small"
                  onChange={(checked) => setContentType(checked ? 3 : 1)}
                  checked={isStyledMarkdown}
                />
              </div>
              <div className="mt-2 text-[13px] leading-[18px] text-white/30">
                {t('Rich tags for Description & Opening')}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="relative min-w-0 text-sm">
              {t('Main Character Gender')}
              <span className="text-[#ff5454]">*</span>
            </div>
            <div className="flex min-w-0 flex-1 justify-end text-[13px]">
              <div
                className={clsx(
                  'relative flex min-w-0 cursor-pointer items-center',
                  charGender === 'male' ? 'text-white' : 'text-[#B3B3B3]'
                )}
                onClick={() => setCharGender('male')}
              >
                {charGender === 'male' ? (
                  <div className="mr-1.5 flex-shrink-0">
                    <CreateSelectedMobileIcon />
                  </div>
                ) : (
                  <div className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 rounded-full border-2 border-white/50"></div>
                )}
                <span className="inline-block min-w-0 break-words leading-tight">
                  {t('Male')}
                </span>
              </div>
              <div
                className={clsx(
                  'relative ml-3.5 flex min-w-0 cursor-pointer items-center',
                  charGender === 'female' ? 'text-white' : 'text-[#B3B3B3]'
                )}
                onClick={() => setCharGender('female')}
              >
                {charGender === 'female' ? (
                  <div className="mr-1.5 flex-shrink-0">
                    <CreateSelectedMobileIcon />
                  </div>
                ) : (
                  <div className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 rounded-full border-2 border-white/50"></div>
                )}
                <span className="inline-block min-w-0 break-words leading-tight">
                  {t('Female')}
                </span>
              </div>
              <div
                className={clsx(
                  'relative ml-3.5 flex min-w-0 cursor-pointer items-center',
                  charGender === 'other' ? 'text-white' : 'text-[#B3B3B3]'
                )}
                onClick={() => setCharGender('other')}
              >
                {charGender === 'other' ? (
                  <div className="mr-1.5 flex-shrink-0">
                    <CreateSelectedMobileIcon />
                  </div>
                ) : (
                  <div className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 rounded-full border-2 border-white/50"></div>
                )}
                <span className="inline-block min-w-0 break-words leading-tight">
                  {t('Non-binary')}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div>
              <span className="relative text-sm">
                {t('Description')} <span className="text-[#ff5454]">*</span>
              </span>
              <span className="inline-block translate-y-[-2px] whitespace-nowrap text-[10px]">
                {/* i18n-ignore */}
                &ensp;|&ensp;&ensp;
              </span>
              <span className="inline-block min-w-0 break-words text-xs leading-tight text-[#999999]">
                {t('Publicly seen')}
              </span>
            </div>

            <div className="relative text-xs text-[#B3B3B3]">
              <span
                style={{
                  color: isDisplayDescriptionError
                    ? '#FF5F5F'
                    : displayDescription.length === 0
                      ? '#B3B3B3'
                      : '#fff',
                }}
              >
                {Intl.NumberFormat('en-US').format(displayDescription.length)}
              </span>
              <span
                style={{
                  color: isDisplayDescriptionError ? '#FF5F5F' : '#B3B3B3',
                }}
              >
                /{Intl.NumberFormat('en-US').format(MAX_SHORT_DESCRIBE_LENGTH)}
              </span>
            </div>
          </div>
          <div
            key={`description-input-${contentType}`}
            className={clsx(
              'relative mt-2 min-h-[60px] rounded-lg bg-white/5 pb-11 pr-11 transition-[min-height] duration-300 ease-out',
              descriptionExpanded ? 'min-h-[320px]' : 'min-h-[60px]'
            )}
          >
            <Input.TextArea
              key={contentType}
              className={clsx(
                'placeholder:text-gray-756A6E overflow-y-auto text-[13px] !leading-[15px]',
                styles.input
              )}
              autoSize={
                descriptionExpanded
                  ? { minRows: 14 }
                  : { minRows: 2, maxRows: 4 }
              }
              variant="borderless"
              style={{
                resize: 'none',
                padding: '10px 12px',
                background: 'rgb(0 ,0 ,0,0)',
                color: '#FFFFFF',
                border: isDisplayDescriptionError
                  ? '1px solid #9c4242'
                  : '0px solid #d9d9d9',
                maxHeight: descriptionExpanded ? '400px' : '120px',
              }}
              placeholder={t(
                contentType === 1
                  ? 'Description of your character, story, or both. This will be displayed on the card and at the top of the chat page'
                  : 'Wrap your HTML using<html-box> </html-box>for JS support and better rendering.'
              )}
              onChange={(e) =>
                isRichDisplayContent
                  ? setStyledTagline(e.target.value)
                  : setCharShortDescribe(e.target.value)
              }
              value={displayDescription}
            />
            <button
              type="button"
              className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
              onClick={() => setDescriptionExpanded((v) => !v)}
              aria-label={descriptionExpanded ? t('Collapse') : t('Expand')}
            >
              {descriptionExpanded ? (
                <ChatInspirationCollapseLightIcon size={12} color="#FFFFFF" />
              ) : (
                <ChatInspirationZoomLightIcon size={18} color="#FFFFFF" />
              )}
            </button>
          </div>
          <div className="h-4 text-xs text-[#ff4d4f]">
            {isCharShortDescribeError && (
              <span>
                {t('please keep your input within {{max}} characters', {
                  max: MAX_SHORT_DESCRIBE_LENGTH,
                })}
              </span>
            )}
          </div>
          <div>
            <div className="relative flex items-center justify-between">
              <div className="text-sm">
                {t('Opening')} <span className="text-[#ff5454]">*</span>
              </div>
              <div className="text-xs text-[#B3B3B3]">
                <span
                  style={{
                    color: isDisplayOpeningError
                      ? '#FF5F5F'
                      : displayOpening.length === 0
                        ? '#B3B3B3'
                        : '#fff',
                  }}
                >
                  {t('Token:')}{' '}
                  {Intl.NumberFormat('en-US').format(greetingTokenCount)}
                </span>
              </div>
            </div>

            <div
              key={`opening-input-${contentType}`}
              className={clsx(
                'relative mt-2 rounded-lg bg-white/5 pb-11 pr-11 transition-[min-height] duration-300 ease-out',
                openingExpanded ? 'min-h-[320px]' : 'min-h-[80px]'
              )}
            >
              <Input.TextArea
                className={clsx(
                  'placeholder:text-gray-756A6E overflow-y-auto text-[13px] !leading-[15px]',
                  styles.input
                )}
                autoSize={
                  openingExpanded ? { minRows: 14 } : { minRows: 3, maxRows: 6 }
                }
                variant="borderless"
                style={{
                  resize: 'none',
                  padding: '10px 12px',
                  background: 'rgb(0 ,0 ,0,0)',
                  color: '#FFFFFF',
                  border: isDisplayOpeningError
                    ? '1px solid #9c4242'
                    : '0px solid #d9d9d9',
                  maxHeight: openingExpanded ? '400px' : '180px',
                }}
                placeholder={t(
                  contentType === 1
                    ? 'Write the opening scenario. Use asterisks for narration and "quotes" for dialogue (e.g., *She enters*. "Hello.").'
                    : 'Wrap all dialogue in <message></message> and all narration/actions in <narration></narration>.'
                )}
                onChange={(e) =>
                  isRichDisplayContent
                    ? setStyledGreeting(e.target.value)
                    : setCharGreeting(e.target.value)
                }
                value={displayOpening}
              />
              <button
                type="button"
                className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
                onClick={() => setOpeningExpanded((v) => !v)}
                aria-label={openingExpanded ? t('Collapse') : t('Expand')}
              >
                {openingExpanded ? (
                  <ChatInspirationCollapseLightIcon size={12} color="#FFFFFF" />
                ) : (
                  <ChatInspirationZoomLightIcon size={18} color="#FFFFFF" />
                )}
              </button>
            </div>
            <div className="h-4 text-xs text-[#ff4d4f]">
              {displayOpening &&
                displayOpening.length < MIN_GREETING_LENGTH && (
                  <span>
                    {t('please input at least {{min}} characters', {
                      min: MIN_GREETING_LENGTH,
                    })}
                  </span>
                )}
              {displayOpening.length > MAX_GREETING_LENGTH && (
                <span>
                  {t('please keep your input within {{max}} characters', {
                    max: MAX_GREETING_LENGTH,
                  })}
                </span>
              )}
            </div>
          </div>
          {ENABLE_WEB_GREETING_VIDEO_CONFIG ? (
            <GreetingVideoUploader
              valueKey={greetingVideoKey}
              valueUrl={greetingVideoUrl}
              uploading={greetingVideoUploading}
              setCharForm={setCharForm}
              variant="mobile"
              className="mb-5"
            />
          ) : null}
          <div className="mb-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="relative text-sm">{t('Background')}</span>
                <span className="inline-block text-[10px]">
                  {/* i18n-ignore */}
                  &ensp;&ensp;|&ensp;&ensp;
                </span>
                <p className="mr-1 text-sm text-[#B3B3B3]">{t('Public')}</p>
                <span className="relative text-xs text-[#999999]">
                  <Switch
                    className={clsx('!min-h-[19px] !min-w-[36px]', {
                      '!bg-white/20': !showCharLongDescribe,
                      '!bg-[#FF8A00]': showCharLongDescribe,
                    })}
                    size="small"
                    onChange={(val) => setShowCharLongDescribe(val)}
                    checked={showCharLongDescribe}
                  />
                </span>
              </div>
              <div className="text-sm text-[#B3B3B3]">
                <span
                  style={{
                    color: isCharLongDescribeError
                      ? '#FF5F5F'
                      : charLongDescribe.length === 0
                        ? '#B3B3B3'
                        : '#fff',
                  }}
                >
                  {t('Token:')}{' '}
                  {Intl.NumberFormat('en-US').format(backgroundTokenCount)}
                </span>
              </div>
            </div>
            <div
              className={clsx(
                'relative mt-2 rounded-lg bg-white/5 pb-11 pr-11 transition-[min-height] duration-300 ease-out',
                backgroundExpanded ? 'min-h-[320px]' : 'min-h-[120px]'
              )}
            >
              <Input.TextArea
                className={clsx(
                  'placeholder:text-gray-756A6E text-xs !leading-[15px]',
                  styles.input
                )}
                autoSize={
                  backgroundExpanded
                    ? { minRows: 14 }
                    : { minRows: 8, maxRows: 8 }
                }
                variant="borderless"
                style={{
                  resize: 'none',
                  padding: '10px 12px',
                  background: 'rgb(0 ,0 ,0,0)',
                  color: '#FFFFFF',
                  border: isCharLongDescribeError
                    ? '1px solid #9c4242'
                    : '0px solid #d9d9d9',
                  maxHeight: backgroundExpanded ? '400px' : '240px',
                }}
                placeholder={t(
                  "Define your characters' appearance, personality, and background. You can also add custom prompts, stories, rules, or upload a script/novel. These details will guide how they act and respond"
                )}
                onChange={(e) => setCharLongDescribe(e.target.value)}
                defaultValue={charLongDescribe}
                value={charLongDescribe}
              />
              <button
                type="button"
                className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
                onClick={() => setBackgroundExpanded((v) => !v)}
                aria-label={backgroundExpanded ? t('Collapse') : t('Expand')}
              >
                {backgroundExpanded ? (
                  <ChatInspirationCollapseLightIcon size={12} color="#FFFFFF" />
                ) : (
                  <ChatInspirationZoomLightIcon size={18} color="#FFFFFF" />
                )}
              </button>
            </div>
            {isCharLongDescribeError && (
              <div className="h-5 text-xs text-[#ff4d4f]">
                <span>
                  {t('please keep your input within {{max}} characters', {
                    max: MAX_LONG_DESCRIBE_LENGTH,
                  })}
                </span>
              </div>
            )}

            {/* HTML 单角色（content_type=2）隐藏整个上传入口：后端对其剧本静默丢弃
                （聊天走 richtext prompt 路径，剧本不注入），世界书入口按产品要求一并收口 */}
            {(character_type === 2 || contentType === 1) && (
              <div className="mt-2" ref={plotUploaderRef}>
                <BackgroundFileUploader
                  allowPlot
                  plotFileName={plot_file_name || undefined}
                  plotFilePrefix={plot_file_prefix || undefined}
                  onPlotUploadSuccess={(key, name, prefix) => {
                    setPlotFileKey(key)
                    setPlotFileName(name)
                    setPlotFilePrefix(prefix)
                  }}
                  onPlotDelete={() => {
                    setPlotFileKey('')
                    setPlotFileName('')
                    setPlotFilePrefix('')
                  }}
                  onPlotUploadStart={() => setIsPlotFileUploading(true)}
                  onPlotUploadEnd={() => setIsPlotFileUploading(false)}
                  worldBooks={world_books || []}
                  onWorldBookUploadSuccess={(key, name, entryCount) =>
                    addWorldBook(key, name, entryCount)
                  }
                  onWorldBookDelete={(key) => removeWorldBook(key)}
                  onWorldBookUploadStart={() => setIsWorldBookUploading(true)}
                  onWorldBookUploadEnd={() => setIsWorldBookUploading(false)}
                  onUploaderClick={() => {
                    trackEvent('plot_uploader_click', { platform }, 'CLK')
                  }}
                  onFileSelected={(fileType, fileSize) => {
                    trackEvent(
                      'plot_file_selected',
                      {
                        file_type: fileType,
                        file_size: fileSize,
                        platform,
                      },
                      'OTHER'
                    )
                  }}
                  onUploadComplete={(fileType, fileSize) => {
                    trackEvent(
                      'plot_upload_success',
                      {
                        file_type: fileType,
                        file_size: fileSize,
                        platform,
                      },
                      'OTHER'
                    )
                  }}
                />
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <div className="relative min-w-0 text-sm tracking-[0.4px]">
              <span className="inline-block min-w-0 break-words leading-tight">
                {t('Rating')} <span className="text-[#ff5454]">*</span>
              </span>
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-end text-[13px]">
              <div
                className={clsx(
                  'relative flex min-w-0 items-center',
                  hasNsfwCharacter
                    ? 'cursor-not-allowed opacity-40'
                    : 'cursor-pointer',
                  !nsfwCheck ? 'text-white' : 'text-[#B3B3B3]'
                )}
                onClick={() => {
                  if (!hasNsfwCharacter) setNsfwCheck(false)
                }}
              >
                {!nsfwCheck ? (
                  <div className="mr-1.5 flex-shrink-0">
                    <CreateSelectedMobileIcon />
                  </div>
                ) : (
                  <div className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 rounded-full border-2 border-white/50"></div>
                )}
                <span className="inline-block min-w-0 break-words leading-tight">
                  {t('Limited')}
                </span>
              </div>
              <div
                className={clsx(
                  'relative ml-7 flex min-w-0 cursor-pointer items-center',
                  nsfwCheck ? 'text-white' : 'text-[#B3B3B3]'
                )}
                onClick={() => setNsfwCheck(true)}
              >
                {nsfwCheck ? (
                  <div className="mr-1.5 flex-shrink-0">
                    <CreateSelectedMobileIcon />
                  </div>
                ) : (
                  <div className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 rounded-full border-2 border-white/50"></div>
                )}
                <span className="inline-block min-w-0 break-words leading-tight">
                  {t('Limitless')}
                </span>
              </div>
            </div>
          </div>
          <div className="relative mt-2 w-full text-[11px] text-[#999999]">
            <div className="mr-[3px] inline-block translate-y-[3px]">
              <CreateRulesIcon />
            </div>
            {t(
              "Select rating carefully based on character's image and descriptions. NSFW characters will NOT appear by default unless NSFW mode is enabled to include appropriate 18+ content"
            )}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="relative min-w-0 text-sm">
              <span className="inline-block min-w-0 break-words leading-tight">
                {t('Visibility')} <span className="text-[#ff5454]">*</span>
              </span>
            </div>
            <div className="flex min-w-0 flex-1 justify-end text-[13px]">
              <div
                className={clsx(
                  'relative ml-7 flex min-w-0 cursor-pointer items-center',
                  charIsPublic === 'true' ? 'text-white' : 'text-[#B3B3B3]'
                )}
                onClick={() => setCharIsPublic('true')}
              >
                {charIsPublic === 'true' ? (
                  <div className="mr-1.5 flex-shrink-0">
                    <CreateSelectedMobileIcon />
                  </div>
                ) : (
                  <div className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 rounded-full border-2 border-white/50"></div>
                )}
                <span className="inline-block min-w-0 break-words leading-tight">
                  {t('Public')}
                </span>
              </div>
              <div
                className={clsx(
                  'relative ml-7 flex min-w-0 cursor-pointer items-center',
                  charIsPublic === 'false' ? 'text-white' : 'text-[#B3B3B3]'
                )}
                onClick={() => setCharIsPublic('false')}
              >
                {charIsPublic === 'false' ? (
                  <div className="mr-1.5 flex-shrink-0">
                    <CreateSelectedMobileIcon />
                  </div>
                ) : (
                  <div className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 rounded-full border-2 border-white/50"></div>
                )}
                <span className="inline-block min-w-0 break-words leading-tight">
                  {t('Private')}
                </span>
              </div>
            </div>
          </div>
          {character_type === 1 && (
            <div className="mt-4">
              <div className="relative mb-2 min-w-0 text-sm">
                <span className="inline-block min-w-0 break-words leading-tight">
                  {t('Sound')}
                </span>
              </div>
              <button
                type="button"
                onClick={() =>
                  router.push(
                    withLocale(
                      `/create/edit-voice?${isUpdateChar ? 'update=true' : ''}${searchParams.get('character_id') ? `&character_id=${searchParams.get('character_id')}` : ''}`,
                      lang
                    )
                  )
                }
                className="flex w-full rounded-lg border border-white/10 bg-white/5 p-2"
              >
                <div className="flex flex-1 flex-row flex-wrap gap-1.5">
                  {charForm.charBaseTone &&
                  selectedVoices &&
                  selectedVoices.length > 0 ? (
                    <div className="flex items-center">
                      {/* 播放按钮 */}
                      <VoicePlayButton
                        voice={selectedVoices[0]}
                        speed={charForm.charVoiceSpeed || 1.0}
                        className="mr-2"
                      />
                      <span className="text-sm leading-8 text-white">
                        {selectedVoices[0].name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm leading-8 text-white/30">
                      {charForm.charBaseTone
                        ? t('Loading...')
                        : t('Select Sound')}
                    </span>
                  )}
                </div>
                <div className="flex h-8 items-center justify-center">
                  <ArrowDownIcon className="rotate-[-90deg]" />
                </div>
              </button>
            </div>
          )}
          <div className="mt-4">
            <span className="relative text-sm">
              {t('Tags')}{' '}
              <span className="text-xs text-[#B3B3B3]">
                {charTagIDs.length}/10
              </span>
            </span>
            <button
              type="button"
              onClick={() => {
                setLocalSelectedTags(charTagIDs)
                setTagDrawerOpen(true)
              }}
              className="mt-2 flex w-full rounded-lg border border-white/10 bg-white/5 p-2"
            >
              <div className="flex flex-1 flex-row flex-wrap gap-1.5">
                {selectedTagItems.length === 0 ? (
                  <span className="text-sm leading-8 text-white/30">
                    {t('Choose relevant tags for your creation')}
                  </span>
                ) : (
                  selectedTagItems.map((tagItem) => (
                    <span
                      key={tagItem.id}
                      className="flex h-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 px-3.5 text-sm text-white/50"
                    >
                      <TagDisplayLabel tag={tagItem} iconSize={16} />
                    </span>
                  ))
                )}
              </div>
              <div className="flex h-8 items-center justify-center">
                <ArrowDownIcon className="rotate-[-90deg]" />
              </div>
            </button>
          </div>
          {character_type === 2 && (
            <div className="mb-5 mt-6">
              <div className="text-sm text-white">
                {t('Character List')}（{selectedRoleList.length || 0}）
              </div>
              <div className="mb-4">
                <span className="text-[13px] opacity-30">
                  {t('Public stories can only include published characters.')}
                </span>
                <span className="text-[13px] opacity-30">
                  {t(
                    "Stories containing other users' characters can only be set to private."
                  )}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-start gap-[22px]">
                {selectedRoleList.map((item) => renderCharacter(item))}
                <div
                  style={{
                    alignItems: 'center',
                  }}
                >
                  <button
                    type="button"
                    className="flex h-[68px] w-[68px] items-center justify-center rounded-full border-2 border-dashed border-white/20 bg-white/5"
                    onClick={() => router.push(roleCharacterHref)}
                  >
                    <PlusOutlined
                      style={{
                        fontSize: '24px',
                        color: 'rgba(255,255,255,0.8)',
                      }}
                    />
                  </button>
                  <div className="flex w-full flex-row items-center justify-center">
                    <div style={{ color: '#fff', fontSize: 13, marginTop: 10 }}>
                      {t('Character')}
                    </div>
                  </div>
                </div>
                {/* 先删除renderUser */}
                {/* {renderUser()} */}
              </div>
            </div>
          )}
          <div className="my-4 flex items-center gap-2.5 text-sm">
            <div>{t('Advanced Settings')}</div>
            <Switch
              className={clsx('!min-h-[19px] !min-w-[36px]', {
                '!bg-white/20': !advancedSetCheck,
                '!bg-[#FF8A00]': advancedSetCheck,
              })}
              size="small"
              onChange={(val) => setAdvancedSetCheck(val)}
              checked={advancedSetCheck}
            />
          </div>
          {advancedSetCheck && (
            <div>
              {contentType === 2 && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="relative text-[15px] tracking-[0.4px]">
                      {t('Layout Customize')}
                    </div>
                    <div className="relative text-sm text-[#B3B3B3]">
                      <span
                        style={{
                          color:
                            optimizationCode.length === 0 ? '#B3B3B3' : '#fff',
                        }}
                      >
                        {Intl.NumberFormat('en-US').format(
                          optimizationCode?.length ?? 0
                        )}
                      </span>
                      <span style={{ color: '#B3B3B3' }}>
                        /
                        {Intl.NumberFormat('en-US').format(
                          MAX_OPTIMIZATION_CODE_LENGTH
                        )}
                      </span>
                    </div>
                  </div>
                  <div
                    className={clsx(
                      'relative mt-2 rounded-lg bg-white/5 pb-11 pr-11 transition-[min-height] duration-300 ease-out',
                      layoutCustomizeExpanded ? 'min-h-[320px]' : 'min-h-[80px]'
                    )}
                  >
                    <Input.TextArea
                      className={clsx(
                        'placeholder:text-gray-756A6E overflow-y-auto text-sm',
                        styles.input
                      )}
                      autoSize={
                        layoutCustomizeExpanded
                          ? { minRows: 14 }
                          : { minRows: 3, maxRows: 6 }
                      }
                      variant="borderless"
                      style={{
                        resize: 'none',
                        background: 'rgb(0 ,0 ,0,0)',
                        color: '#FFFFFF',
                        border: '0px solid #d9d9d9',
                        maxHeight: layoutCustomizeExpanded ? '400px' : '180px',
                      }}
                      placeholder={t(
                        'Write your global CSS code here to customize your chat layout:\n<style> Insert CSS code here </style>'
                      )}
                      onChange={(e) => setOptimizationCode(e.target.value)}
                      value={optimizationCode}
                      maxLength={MAX_OPTIMIZATION_CODE_LENGTH}
                    />
                    <button
                      type="button"
                      className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
                      onClick={() => setLayoutCustomizeExpanded((v) => !v)}
                      aria-label={
                        layoutCustomizeExpanded ? t('Collapse') : t('Expand')
                      }
                    >
                      {layoutCustomizeExpanded ? (
                        <ChatInspirationCollapseLightIcon
                          size={12}
                          color="#FFFFFF"
                        />
                      ) : (
                        <ChatInspirationZoomLightIcon
                          size={18}
                          color="#FFFFFF"
                        />
                      )}
                    </button>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="relative text-[15px] tracking-[0.4px]">
                      {t('Output Setting')}
                    </div>
                    <div className="relative text-sm text-[#B3B3B3]">
                      <span
                        style={{
                          color:
                            outputFormatConfig.length === 0
                              ? '#B3B3B3'
                              : '#fff',
                        }}
                      >
                        {t('Token:')}{' '}
                        {Intl.NumberFormat('en-US').format(
                          outputSettingTokenCount
                        )}
                      </span>
                    </div>
                  </div>
                  <div
                    className={clsx(
                      'relative mt-2 rounded-lg bg-white/5 pb-11 pr-11 transition-[min-height] duration-300 ease-out',
                      outputSettingExpanded ? 'min-h-[320px]' : 'min-h-[80px]'
                    )}
                  >
                    <Input.TextArea
                      className={clsx(
                        'placeholder:text-gray-756A6E overflow-y-auto text-sm',
                        styles.input
                      )}
                      autoSize={
                        outputSettingExpanded
                          ? { minRows: 14 }
                          : { minRows: 3, maxRows: 6 }
                      }
                      variant="borderless"
                      style={{
                        resize: 'none',
                        background: 'rgb(0 ,0 ,0,0)',
                        color: '#FFFFFF',
                        border: '0px solid #d9d9d9',
                        maxHeight: outputSettingExpanded ? '400px' : '180px',
                      }}
                      placeholder={t(
                        "Customize the character's output style (HTML/CSS) and narrative settings. Options include narrative pacing, dialogue-to-narration ratio, plot intensity, etc."
                      )}
                      onChange={(e) => setOutputFormatConfig(e.target.value)}
                      value={outputFormatConfig}
                    />
                    <button
                      type="button"
                      className="absolute bottom-2.5 right-2.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/20 hover:bg-black/30"
                      onClick={() => setOutputSettingExpanded((v) => !v)}
                      aria-label={
                        outputSettingExpanded ? t('Collapse') : t('Expand')
                      }
                    >
                      {outputSettingExpanded ? (
                        <ChatInspirationCollapseLightIcon
                          size={12}
                          color="#FFFFFF"
                        />
                      ) : (
                        <ChatInspirationZoomLightIcon
                          size={18}
                          color="#FFFFFF"
                        />
                      )}
                    </button>
                  </div>
                </>
              )}

              <div className="mt-4 items-center justify-between">
                <div className="relative text-base tracking-[0.4px]">
                  {t('Conversation Style')}
                </div>
                <div
                  className="my-2 flex h-11 items-center justify-between rounded-lg border border-white/10 bg-white/5 pl-3 pr-3 text-sm text-white/30"
                  onClick={() => setConversationDrawerOpen(true)}
                >
                  {
                    conversationStyleData?.find(
                      (item) => item.id === conversationStyleKey
                    )?.name
                  }
                  <span className="rotate-180">
                    <NavBackOutlinedMobileIcon />
                  </span>
                </div>
                {/* <div className="flex text-sm">
                {conversationStyleData?.map((item) => (
                  <div
                    key={item.id}
                    className={clsx(
                      'relative ml-5 flex cursor-pointer items-center',
                      conversationStyleKey === item.id
                        ? 'text-white'
                        : 'text-[#B3B3B3]'
                    )}
                    onClick={() => setConversationStyleKey(item.id)}
                  >
                    {conversationStyleKey === item.id ? (
                      <div className="mr-2.5">
                        <CreateSelectedIcon />
                      </div>
                    ) : (
                      <div className="mr-2.5 h-5 w-5 rounded-full border-2 border-white/50"></div>
                    )}
                    {item.name}
                    <Tooltip
                      title={item.intro}
                      overlayInnerStyle={{
                        fontSize: '12px',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        boxShadow: '0px 2px 4px 0px rgba(255,255,255,0.05)',
                        borderRadius: '8px',
                        // border: '1px solid rgba(255,255,255,0.4)',
                      }}
                    >
                      <CreatePromptIcon />
                    </Tooltip>
                  </div>
                ))}
              </div> */}
              </div>

              {/* 暂时隐藏 Scenario */}
              {/* <div className="relative flex items-center justify-between">
              <div className="text-[15px] tracking-[0.4px]">Scenario</div>
              <div className="text-sm text-[#B3B3B3]">
                <span
                  style={{
                    color: isScenarioError
                      ? '#FF5F5F'
                      : charScenario.length === 0
                        ? '#B3B3B3'
                        : '#fff',
                  }}
                >
                  {charScenario.length}
                </span>
                <span
                  style={{
                    color: isScenarioError ? '#FF5F5F' : '#B3B3B3',
                  }}
                >
                  /{Intl.NumberFormat('en-US').format(MAX_SCENARIO_LENGTH)}
                </span>
              </div>
            </div>
            <div className="mt-2 min-h-[80px] rounded-lg bg-white/5">
              <Input.TextArea
                className={clsx(
                  'placeholder:text-gray-756A6E overflow-y-hidden text-sm',
                  styles.input
                )}
                autoSize
                variant="borderless"
                style={{
                  resize: 'none',
                  background: 'rgb(0 ,0 ,0,0)',
                  color: '#FFFFFF',
                  border: isScenarioError
                    ? '1px solid #9c4242'
                    : '0px solid #d9d9d9',
                }}
                placeholder="Describe the current situation and context of the conversation"
                onChange={(e) => setCharScenario(e.target.value)}
                defaultValue={charScenario}
                value={charScenario}
              />
            </div>
            <div className="h-4 text-sm text-[#ff4d4f]">
              {isScenarioError && (
                <span>
                  {t('please keep your input within {{max}} characters', {
                    max: MAX_SCENARIO_LENGTH,
                  })}
                </span>
              )}
              </div> */}

              <div className="mt-4 flex items-center justify-between">
                <div className="relative text-[15px] tracking-[0.4px]">
                  {t('Example Dialogues')}
                </div>

                <div className="relative text-sm text-[#B3B3B3]">
                  <span
                    style={{
                      color: isCharDialogStyleError
                        ? '#FF5F5F'
                        : charDialogStyleFirstAllRoleMessage.length === 0
                          ? '#B3B3B3'
                          : '#fff',
                    }}
                  >
                    {Intl.NumberFormat('en-US').format(
                      charDialogStyleFirstAllRoleMessage?.length ?? 0
                    )}
                  </span>
                  <span
                    style={{
                      color: isCharDialogStyleError ? '#FF5F5F' : '#B3B3B3',
                    }}
                  >
                    /
                    {Intl.NumberFormat('en-US').format(MAX_DIALOG_STYLE_LENGTH)}
                  </span>
                </div>
              </div>
              <div className="mt-2 min-h-20 rounded-lg bg-white/5">
                <Input.TextArea
                  className={clsx(
                    'placeholder:text-gray-756A6E overflow-y-auto text-sm',
                    styles.input
                  )}
                  variant="borderless"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  style={{
                    resize: 'none',
                    background: 'rgb(0 ,0 ,0,0)',
                    color: '#FFFFFF',
                    maxHeight: '180px',
                    border: isCharDialogStyleError
                      ? '1px solid #9c4242'
                      : '0px solid #d9d9d9',
                  }}
                  placeholder={t('Example Dialogue')}
                  onChange={(e) => handleDialogInputChange(e.target.value)}
                  defaultValue={charDialogStyleFirstAllRoleMessage}
                  value={charDialogStyleFirstAllRoleMessage}
                />
              </div>
              <div className="h-5 text-sm text-[#ff4d4f]">
                {isCharDialogStyleError && (
                  <span>
                    {t('please keep your input within {{max}} characters', {
                      max: MAX_DIALOG_STYLE_LENGTH,
                    })}
                  </span>
                )}
              </div>
              {replySettingBlock}
            </div>
          )}
        </div>
        {/* button部分 */}
        <div
          className="w-full shrink-0"
          style={{
            marginLeft: `calc(${MOBILE_PAGE_PADDING} * -1)`,
            marginRight: `calc(${MOBILE_PAGE_PADDING} * -1)`,
            background: `linear-gradient(
                  to right bottom,
                  rgba(48, 50, 41, 0.75),
                  rgba(61, 44, 41, 0.85),
                  rgba(67, 46, 60, 0.70)
                )`,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 -1px 12px rgba(0, 0, 0, 0.1)',
          }}
        >
          {!isUpdateChar && (
            <CharacterCreationConfirmation
              checked={isCreationConfirmed}
              hasError={showCreationConfirmationError}
              onCheckedChange={setCreationConfirmed}
              className="px-3 pt-3"
            />
          )}
          <div className="flex gap-3 p-2">
            <button
              type="button"
              onClick={() => setDraftModalOpen(true)}
              aria-label={t('Draft Box')}
              className="flex h-[42px] w-[42px] shrink-0 items-center justify-center"
            >
              <Image
                src="/create/creator-draft.png"
                alt=""
                width={42}
                height={42}
                className="h-[42px] w-[42px]"
              />
            </button>
            <TipsyButton
              className="relative h-11 flex-1"
              onClick={handleSaveCurrentDraft}
              variant="filled"
              loading={draftSaving}
              disabled={greetingVideoUploading}
            >
              {t('Save')}
            </TipsyButton>
            <TipsyButton
              className="relative h-11 flex-1"
              onClick={goNextStep}
              variant="filled"
              disabled={
                (!isUpdateChar && !isCreationConfirmed) ||
                nextButtonDisabled ||
                charIsPublic === '' ||
                hasProfileLock ||
                greetingVideoUploading
              }
              loading={createdLoading}
            >
              {hasProfileLock ? t('Contains Private Characters') : t('Confirm')}
            </TipsyButton>
          </div>
          <div className="pb-[env(safe-area-inset-bottom,0)]" />
        </div>

        <CharacterDraftModal
          mobile
          open={draftModalOpen}
          total={draftTotal}
          loading={draftLoading}
          items={draftItems}
          deletingDraftId={draftDeletingId}
          onClose={() => setDraftModalOpen(false)}
          onLoad={handleLoadDraftById}
          onDelete={deleteDraftById}
          onExposure={characterAnalytics.draftBoxExposure}
        />
        <RestoreLocalDraftModal
          mobile
          open={restoreModalOpen}
          onRestore={handleRestoreCache}
          onDiscard={discardCache}
        />

        <Drawer
          title={null}
          placement="bottom"
          width="100%"
          open={tagDrawerOpen}
          maskClosable
          onClose={() => setTagDrawerOpen(false)}
          className="rounded-t-2xl px-2.5 shadow-[0_-1px_3px_0_#664955]"
          style={{
            background: `linear-gradient(
                to right,
                rgba(48, 50, 41, 0.75),
                rgba(61, 44, 41, 0.85),
                rgba(67, 46, 60, 0.70)
              )`,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: '0 -1px 12px rgba(0, 0, 0, 0.1)',
          }}
          rootClassName="md:!hidden"
          height={540}
          closeIcon={null}
          styles={{ body: { padding: 0 } }}
        >
          <div className="flex h-full w-full flex-col">
            <div className="flex justify-between">
              <div className="py-3 text-base text-white">{t('Tags')}</div>
              <button
                className="flex items-center justify-center"
                onClick={() => setTagDrawerOpen(false)}
              >
                <CreateExitMobileIcon />
              </button>
            </div>
            <div className="max-h-[390px] flex-1 overflow-y-auto">
              <div className="relative mt-3">
                {visibleTagOptions.map((tagItem) => {
                  const { id, isNew, isEvent } = tagItem

                  return (
                    <button
                      className={clsx(
                        'relative mb-2 mr-2 inline-flex h-[30px] cursor-pointer select-none items-center gap-1 rounded-full border px-[14px] align-middle text-xs tracking-wide text-white hover:border-white/40',
                        {
                          'border-white/40 bg-white/20':
                            localSelectedTags.includes(id),
                          'border-white/5 bg-white/5':
                            !localSelectedTags.includes(id),
                        }
                      )}
                      onClick={() => {
                        if (localSelectedTags.includes(id)) {
                          setLocalSelectedTags(
                            localSelectedTags.filter((tag) => tag !== id)
                          )
                        } else {
                          if (localSelectedTags.length >= 10) {
                            message.error({
                              content: t(
                                'Select up to 10 relevant tags or keywords'
                              ),
                              icon: <ExclamationCircleOutlined />,
                            })
                            return
                          }
                          setLocalSelectedTags([...localSelectedTags, id])
                        }
                      }}
                      key={id}
                    >
                      {isNew && (
                        <div className="absolute right-[-8px] top-[-6px]">
                          <TagNewIcon />
                        </div>
                      )}
                      {isEvent && (
                        <div className="absolute right-[-8px] top-[-6px]">
                          <TagEventIcon />
                        </div>
                      )}
                      <TagDisplayLabel tag={tagItem} iconSize={18} />
                    </button>
                  )
                })}
              </div>
            </div>
            <div>
              <TipsyButton
                className="my-4 h-[44px] w-full text-[15px] text-white"
                variant="filled"
                onClick={() => {
                  setTagDrawerOpen(false)
                  setCharTagIDs(localSelectedTags)
                }}
              >
                {t('Confirm')}
              </TipsyButton>
              <div className="pb-[env(safe-area-inset-bottom,0)]" />
            </div>
          </div>
        </Drawer>
        <Drawer
          title={null}
          placement="bottom"
          width="100%"
          open={conversationDrawerOpen}
          maskClosable
          onClose={() => setConversationDrawerOpen(false)}
          className="rounded-t-2xl px-2.5 shadow-[0_-1px_3px_0_#664955]"
          style={{
            background: `linear-gradient(
            to right bottom,
            rgba(48, 50, 41, 0.75),
            rgba(61, 44, 41, 0.85),
            rgba(67, 46, 60, 0.70)
          )`,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: '0 -1px 12px rgba(0, 0, 0, 0.1)',
          }}
          rootClassName="md:!hidden"
          height={540}
          closeIcon={null}
          styles={{ body: { padding: 0 } }}
        >
          <div className="flex h-full w-full flex-col">
            <div className="py-5 text-base text-white">
              {t('Conversation Style')}
            </div>
            <div className="flex-1">
              {conversationStyleData?.map((item, index) => (
                <div key={item.id}>
                  <div
                    className="flex justify-between"
                    onClick={() => setConversationStyleKey(item.id)}
                  >
                    <div className="flex-1 pl-1">
                      <div className="text-sm text-white">{item.name}</div>
                      <div className="text-xs text-[#B3B3B3]">{item.intro}</div>
                    </div>
                    {conversationStyleKey === item.id ? (
                      <div className="mr-2.5 h-5 w-5">
                        <CreateSelectedIcon />
                      </div>
                    ) : (
                      <div className="mr-2.5 h-5 w-5 rounded-full border-2 border-white/50"></div>
                    )}
                  </div>
                  {index !== conversationStyleData.length - 1 && (
                    <Divider className="my-4 bg-[rgba(217,217,217,0.1)]" />
                  )}
                </div>
              ))}
            </div>
            <div className="justify-self-end">
              <TipsyButton
                className="my-4 h-[44px] w-full text-[15px] text-white"
                variant="filled"
                onClick={() => setConversationDrawerOpen(false)}
              >
                {t('Confirm')}
              </TipsyButton>
              <div className="pb-[env(safe-area-inset-bottom,0)]" />
            </div>
          </div>
        </Drawer>
      </div>
    </AudioProvider>
  )
}
